<script lang="ts">
  import { onMount } from 'svelte';
  import type { Organization, PublicConfig } from './api';
  import { canEmbedApproval } from './signerApproval';
  import ConnectionQR from './ConnectionQR.svelte';
  import { authenticate } from './auth';
  import { beginNostrConnect, connectBunker, connectNIP07, connectNcryptsec, connectNsec, type PendingNostrConnect, type SignerConnection } from './signers';

  let {
    config,
    managedTenantID = '',
		initialBunker = '',
    onconnected,
    onclose
  }: {
    config: PublicConfig;
    managedTenantID?: string;
		initialBunker?: string;
    onconnected: (connection: SignerConnection, organization: Organization) => void | Promise<void>;
    onclose: () => void;
  } = $props();

  const managedSignerHref = $derived(config.managed_signer_url && managedTenantID ? `${config.managed_signer_url}/?tenant=organization&tenant_id=${encodeURIComponent(managedTenantID)}&return_to=${encodeURIComponent(location.pathname + location.search)}` : '');
  const bitcoinPlusPlusSignInHref = $derived(config.bitcoinplusplus_enabled ? `/api/auth/btcpp/start?return_to=${encodeURIComponent(location.pathname + location.search)}` : '');
  const hasBitcoinPlusPlusOption = $derived(Boolean(managedSignerHref || bitcoinPlusPlusSignInHref));

  let mode = $state<'choose' | 'nsec' | 'unlock' | 'bunker' | 'connect'>('choose');
  let bunkerInput = $state('');
  let nsecInput = $state('');
  let rememberNsec = $state(false);
  let nsecPassphrase = $state('');
  let unlockPassphrase = $state('');
  let savedNcryptsec = $state('');
  let pending = $state<PendingNostrConnect | null>(null);
  let busy = $state(false);
  let connectionStatus = $state('Connecting to vault…');
  let approvalURL = $state('');
  let embeddedApproval = $state(false);
  let error = $state('');
  let copied = $state(false);
  let vaultState = $state<'checking' | 'existing' | 'new' | 'unknown'>('checking');
  let managedBunkerURL = $state('');
  let vaultLookup: AbortController | null = null;
  let activeAttempt: AbortController | null = null;
  let cancelConnection: (() => void) | null = null;
  let dialog: HTMLElement;
  let nip07Available = $state(typeof window !== 'undefined' && Boolean(window.nostr));

  onMount(() => {
		savedNcryptsec = localStorage.getItem('btcpp.badges.ncryptsec') ?? '';
		if (initialBunker) {
			mode = 'bunker';
			bunkerInput = initialBunker;
      vaultState = 'existing';
      managedBunkerURL = initialBunker;
		}
    else if (managedSignerHref) void checkManagedVault();
    const previous = document.activeElement as HTMLElement | null;
    document.body.style.overflow = 'hidden';
    dialog.focus();
    return () => {
      vaultLookup?.abort();
      vaultLookup = null;
      document.body.style.overflow = '';
      previous?.focus();
    };
  });

  async function checkManagedVault() {
    vaultLookup?.abort();
    const controller = new AbortController();
    vaultLookup = controller;
    vaultState = 'checking';
    error = '';
    const timeout = window.setTimeout(() => controller.abort(), 10000);
    try {
      const url = new URL('/api/identities/status', config.managed_signer_url);
      url.search = new URLSearchParams({ tenant: 'organization', tenant_id: managedTenantID }).toString();
      const response = await fetch(url, { credentials: 'omit', signal: controller.signal });
      const result = await response.json();
      if (!response.ok) throw new Error('Vault status unavailable');
      if (result.state === 'new') vaultState = 'new';
      else if (result.state === 'existing' && typeof result.bunker_url === 'string' && result.bunker_url.startsWith('bunker://')) {
        managedBunkerURL = result.bunker_url;
        vaultState = 'existing';
      } else throw new Error('Invalid vault status');
    } catch {
      if (vaultLookup === controller) {
        vaultState = 'unknown';
        error = 'Could not check your organization’s key vault. Retry to connect or check whether setup is needed.';
      }
    } finally {
      window.clearTimeout(timeout);
    }
  }

  async function finish(connectionPromise: Promise<SignerConnection>, controller = new AbortController()) {
    activeAttempt = controller;
    busy = true;
    error = '';
    approvalURL = '';
    embeddedApproval = false;
    let connection: SignerConnection | null = null;
    try {
      connection = await connectionPromise;
      controller.signal.throwIfAborted();
      approvalURL = '';
      embeddedApproval = false;
      connectionStatus = 'Signing in to Badge Studio…';
      const organization = await authenticate(connection, controller.signal);
			if (connection.ncryptsec) {
				localStorage.setItem('btcpp.badges.ncryptsec', connection.ncryptsec);
				savedNcryptsec = connection.ncryptsec;
			}
			await onconnected(connection, organization);
    } catch (cause) {
      await connection?.close();
      if (!controller.signal.aborted) {
        error = cause instanceof Error ? cause.message : 'The signer connection failed.';
      }
    } finally {
      if (activeAttempt === controller) activeAttempt = null;
      busy = false;
      approvalURL = '';
    embeddedApproval = false;
    }
  }

  function startNostrConnect() {
    mode = 'connect';
    error = '';
    try {
      pending = beginNostrConnect(config.signer_relays, undefined, undefined, requestApproval);
      const controller = new AbortController();
      cancelConnection = pending.cancel;
      void finish(pending.connected, controller);
    } catch (cause) {
      error = cause instanceof Error ? cause.message : 'Could not start the remote signer connection.';
    }
  }

  async function copyURI() {
    if (!pending) return;
    await navigator.clipboard.writeText(pending.uri);
    copied = true;
  }

  function close() {
    activeAttempt?.abort();
    cancelConnection?.();
    pending?.cancel();
    onclose();
  }

  function connectExistingBunker() {
    const controller = new AbortController();
    connectionStatus = 'Connecting to vault…';
    void finish(connectBunker(bunkerInput, controller.signal, undefined, requestApproval), controller);
  }

  function requestApproval(url: string) {
    if (!activeAttempt || activeAttempt.signal.aborted) return false;
    approvalURL = url;
    connectionStatus = 'Waiting for your approval…';
    embeddedApproval = canEmbedApproval(url, config.bitcoinplusplus_url);
    return embeddedApproval;
  }

  function connectLocalNsec() {
    const value = nsecInput;
    nsecInput = '';
		const passphrase = rememberNsec ? nsecPassphrase : '';
		nsecPassphrase = '';
    void finish(connectNsec(value, passphrase));
  }

  function unlockSavedNsec() {
		const passphrase = unlockPassphrase;
		unlockPassphrase = '';
		void finish(connectNcryptsec(savedNcryptsec, passphrase));
	}

  function forgetSavedNsec() {
		localStorage.removeItem('btcpp.badges.ncryptsec');
		savedNcryptsec = '';
		unlockPassphrase = '';
		mode = 'choose';
	}

  function keydown(event: KeyboardEvent) {
    if (event.key === 'Escape') {
      close();
      return;
    }
    if (event.key !== 'Tab') return;
    const focusable = Array.from(dialog.querySelectorAll<HTMLElement>('button:not(:disabled), a[href], summary, textarea, input, iframe')).filter((item) => item.offsetParent !== null);
    if (focusable.length === 0) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
    else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
  }
</script>

<svelte:window onkeydown={keydown} />

<div class="backdrop" role="presentation" onclick={(event) => event.currentTarget === event.target && close()}>
  <div class="dialog" class:approval-dialog={embeddedApproval} bind:this={dialog} role="dialog" aria-modal="true" aria-labelledby="signer-title" tabindex="-1">
    {#if embeddedApproval && approvalURL}
      <section class="embedded-approval">
        <header>
          <div><h2 id="signer-title">Approve your connection</h2><p>Review and unlock your Bitcoin++ signer here.</p></div>
          <button class="secondary" type="button" onclick={close}>Cancel</button>
        </header>
        <iframe title="Bitcoin++ signer approval" src={approvalURL}></iframe>
        <footer><span>Having trouble signing in?</span> <a href={approvalURL} target="_blank" rel="noopener noreferrer" onclick={() => embeddedApproval = false}>Open approval in a new tab ↗</a></footer>
      </section>
    {:else}
    <aside class="trust-panel">
      <span class="folio">ACCESS / 01</span>
      <div class="key-seal" aria-hidden="true"><span>⌁</span></div>
      <div>
        <strong>Your key stays yours.</strong>
        <p>Badge Studio only asks your signer to approve specific events. Your private key is never shared.</p>
      </div>
      <span class="protocol">NIP-07 / NIP-46 / NIP-98</span>
    </aside>

    <section class="connection-panel">
      <button class="close" type="button" aria-label="Close" onclick={close}>×</button>
      <p class="eyebrow">organization access</p>
      <h2 id="signer-title">{mode === 'choose' ? 'Choose your signer.' : mode === 'nsec' ? 'Use an nsec locally.' : mode === 'unlock' ? 'Unlock your saved key.' : mode === 'bunker' && initialBunker ? 'Connect your Bitcoin++ key vault.' : mode === 'bunker' ? 'Connect a remote signer.' : 'Approve the connection.'}</h2>
		<p class="intro">{mode === 'choose' ? 'Sign a one-time login request with the key your organization uses to issue badges.' : mode === 'nsec' ? 'The plaintext key stays in this tab’s memory, is never uploaded, and is cleared when you disconnect.' : mode === 'unlock' ? 'Your NIP-49 encrypted key is stored in this browser. The passphrase never leaves this tab.' : mode === 'bunker' && initialBunker ? 'Connect this organization’s vault to sign badges in Badge Studio. Its connection details are already filled in. You’ll approve the connection with bitcoin++ and unlock the vault when prompted.' : mode === 'bunker' ? 'Paste the connection address supplied by your signer. No private key should ever be pasted here.' : 'Send this request to your remote signer, then approve the badge permissions there.'}</p>

      {#if mode === 'choose'}
        {#if managedSignerHref}
          {#if vaultState === 'new'}
          <a class="managed-method" href={managedSignerHref}>
            <span class="method-index">B++</span>
            <span class="method-copy"><strong>Set up a Bitcoin++ key vault</strong><small>Your organization doesn’t have a vault yet. Create or import a key to get started.</small></span>
            <i aria-hidden="true">→</i>
          </a>
          {:else}
          <button class="managed-method" type="button" disabled={busy || vaultState === 'checking'} onclick={() => { if (vaultState === 'existing') { bunkerInput = managedBunkerURL; connectExistingBunker(); } else void checkManagedVault(); }}>
            <span class="method-index">B++</span>
            <span class="method-copy"><strong>{busy ? connectionStatus : vaultState === 'checking' ? 'Checking key vault…' : vaultState === 'unknown' ? 'Retry vault check' : 'Use Bitcoin++ key vault'}</strong><small>{vaultState === 'existing' ? 'recommended · connect and approve here in Badge Studio' : 'Checking whether your organization has a vault or needs setup.'}</small></span>
            <i aria-hidden="true">→</i>
          </button>
          {/if}
        {:else if bitcoinPlusPlusSignInHref}
          <a class="managed-method" href={bitcoinPlusPlusSignInHref}>
            <span class="method-index">B++</span>
            <span class="method-copy"><strong>Sign in with Bitcoin++</strong><small>recommended · choose your organization and connect its key vault</small></span>
            <i aria-hidden="true">→</i>
          </a>
        {/if}
        <details class="alternative-methods" open={!hasBitcoinPlusPlusOption}>
          <summary><span>{hasBitcoinPlusPlusOption ? 'use another signer' : 'signer options'}</span><small>NIP-07 · NIP-46 · local nsec</small><i aria-hidden="true">⌄</i></summary>
          <div class="methods">
            {#if savedNcryptsec}<button type="button" onclick={() => mode = 'unlock'} disabled={busy}><span class="method-index">00</span><span class="method-copy"><strong>Unlock saved key</strong><small>NIP-49 · encrypted in this browser</small></span><i aria-hidden="true">→</i></button>{/if}
            <button type="button" onclick={() => finish(connectNIP07())} disabled={busy || !nip07Available}>
              <span class="method-index">01</span>
              <span class="method-copy"><strong>Browser extension</strong><small>{nip07Available ? 'NIP-07 · ready in this browser' : 'NIP-07 · no extension detected'}</small></span>
              <i aria-hidden="true">→</i>
            </button>
            <button type="button" onclick={() => mode = 'bunker'} disabled={busy}>
              <span class="method-index">02</span>
              <span class="method-copy"><strong>Existing remote signer</strong><small>NIP-46 · bunker URL or NIP-05</small></span>
              <i aria-hidden="true">→</i>
            </button>
            <button type="button" onclick={startNostrConnect} disabled={busy}>
              <span class="method-index">03</span>
              <span class="method-copy"><strong>New connection request</strong><small>NIP-46 · approve in another signer app</small></span>
              <i aria-hidden="true">→</i>
            </button>
            <button type="button" onclick={() => mode = 'nsec'} disabled={busy}>
              <span class="method-index">04</span>
              <span class="method-copy"><strong>Paste an nsec</strong><small>local to this tab · never uploaded</small></span>
              <i aria-hidden="true">→</i>
            </button>
          </div>
        </details>
      {:else if mode === 'nsec'}
        <label for="local-nsec">Nostr secret key</label>
        <input id="local-nsec" class="secret-input" type="password" bind:value={nsecInput} autocomplete="off" autocapitalize="none" spellcheck="false" placeholder="nsec1…" />
        <p class="field-note"><span>!</span> Anyone with this key controls the account. Use it only on a device you trust.</p>
		<label class="remember"><input type="checkbox" bind:checked={rememberNsec} /> Remember an encrypted copy in this browser</label>
		{#if rememberNsec}<label for="nsec-passphrase">Encryption passphrase</label><input id="nsec-passphrase" class="secret-input" type="password" bind:value={nsecPassphrase} autocomplete="new-password" /><p class="field-note"><span>!</span> A forgotten passphrase cannot be recovered.</p>{/if}
        <div class="actions"><button class="secondary" type="button" onclick={() => { nsecInput = ''; nsecPassphrase = ''; mode = 'choose'; }} disabled={busy}>Back</button><button class="primary" type="button" onclick={connectLocalNsec} disabled={busy || !nsecInput.trim() || (rememberNsec && !nsecPassphrase)}>{busy ? 'Signing locally…' : 'Use this key'} <span>→</span></button></div>
      {:else if mode === 'unlock'}
		<label for="unlock-passphrase">Encryption passphrase</label><input id="unlock-passphrase" class="secret-input" type="password" bind:value={unlockPassphrase} autocomplete="current-password" />
		<div class="actions"><button class="secondary" type="button" onclick={forgetSavedNsec}>Forget saved key</button><button class="primary" type="button" onclick={unlockSavedNsec} disabled={busy || !unlockPassphrase}>Unlock <span>→</span></button></div>
      {:else if mode === 'bunker'}
        {#if initialBunker}
          <p class="field-note">Your vault connection is ready. No link needs to be pasted.</p>
          <details><summary>Vault connection details</summary><label for="bunker">Bunker URL</label><textarea id="bunker" value={bunkerInput} readonly rows="4"></textarea></details>
        {:else}
          <label for="bunker">Bunker URL or NIP-05 address</label>
          <textarea id="bunker" bind:value={bunkerInput} placeholder="bunker://… or signer@example.com" rows="4"></textarea>
          <p class="field-note"><span>!</span> Use a connection address only—never an nsec.</p>
        {/if}
        <div class="actions">
          <button class="secondary" type="button" onclick={() => mode = 'choose'} disabled={busy}>Back</button>
		  <button class="primary" type="button" onclick={connectExistingBunker} disabled={busy || !bunkerInput.trim()}>{busy ? connectionStatus : initialBunker ? 'Connect key vault' : 'Connect signer'} <span>→</span></button>
        </div>
    {:else if pending}
      <div class="connect-uri">
        <ConnectionQR value={pending.uri} />
        <code>{pending.uri}</code>
          <div class="connect-actions">
            <a class="primary" href={pending.uri}>Open in signer <span>↗</span></a>
            <button class="secondary" type="button" onclick={copyURI}>{copied ? 'Connection copied' : 'Copy connection'}</button>
          </div>
          <span class="waiting"><i></i> Waiting for your signer to approve…</span>
        </div>
      {/if}

      {#if busy && approvalURL}
        <div class="approval" aria-live="polite">
          <p class="field-note">If the approval page didn’t open, tap below. Approve in the new tab, then return here to finish signing in.</p>
          <div class="actions"><a class="primary" href={approvalURL} target="_blank" rel="noopener noreferrer">Open approval page <span>↗</span></a></div>
        </div>
      {/if}
      {#if busy && mode === 'choose'}<p class="waiting"><i></i> Waiting for signer approval…</p>{/if}
      {#if error}<p class="error" role="alert"><strong>Connection problem</strong>{error}</p>{/if}
    </section>
    {/if}
  </div>
</div>

<style>
  .dialog.approval-dialog { display: block; width: min(850px, 100%); height: min(900px, 95dvh); min-height: 0; }
  .embedded-approval { display: flex; flex-direction: column; height: 100%; min-height: 0; }
  .embedded-approval header { display: flex; align-items: center; justify-content: space-between; gap: 12px; padding: 16px 20px; }
  .embedded-approval h2 { font-size: 24px; max-width: none; }
  .embedded-approval header p, .embedded-approval footer { margin: 8px 0 0; font-size: 13px; line-height: 1.5; }
  .embedded-approval iframe { display: block; flex: 1; width: 100%; min-height: 0; border: 0; background: #fffaf3; }
  .embedded-approval footer { margin: 0; padding: 12px 20px calc(12px + env(safe-area-inset-bottom)); }
  .embedded-approval footer a { color: var(--violet-dark); font-weight: 700; }

  .backdrop { position: fixed; z-index: 10; inset: 0; display: grid; place-items: center; padding: 24px; background: rgba(24, 26, 40, .72); backdrop-filter: blur(10px); }
  .dialog { width: min(850px, 100%); min-height: 525px; display: grid; grid-template-columns: 255px minmax(0, 1fr); overflow: hidden; border: 1px solid rgba(255,255,255,.15); background: var(--surface); box-shadow: 0 30px 110px rgba(9,12,28,.4); }
  .trust-panel { display: flex; flex-direction: column; padding: 27px 25px 25px; background: linear-gradient(155deg, #d9eee8, #dfd1e8); border-right: 1px solid var(--line-strong); }
  .folio, .protocol { color: #675e55; font: 8px ui-monospace, SFMono-Regular, Menlo, monospace; letter-spacing: .14em; }
  .key-seal { display: grid; place-items: center; width: 136px; height: 136px; margin: 54px auto 42px; border: 1px solid var(--violet-dark); border-radius: 50%; background: linear-gradient(145deg, var(--aqua), var(--teal)); box-shadow: inset 0 0 0 10px #dfd9e5, inset 0 0 0 11px var(--violet); }
  .key-seal span { display: grid; place-items: center; width: 73px; height: 73px; border: 1px solid #2a241e; border-radius: 50%; font-family: Georgia, serif; font-size: 48px; transform: rotate(-12deg); }
  .trust-panel div:nth-child(3) { margin-top: auto; }
  .trust-panel strong { font-family: Georgia, serif; font-size: 22px; font-weight: 400; }
  .trust-panel p { margin: 9px 0 30px; color: #665d55; font-size: 12px; line-height: 1.55; }
  .protocol { margin-top: auto; }

  .connection-panel { position: relative; padding: 46px clamp(30px, 5vw, 58px) 40px; }
  .close { position: absolute; top: 17px; right: 20px; display: grid; place-items: center; width: 36px; height: 36px; border: 1px solid #c9beb2; border-radius: 50%; background: transparent; color: #6f665d; font-size: 22px; line-height: 1; cursor: pointer; }
  .eyebrow { margin: 0 0 14px; color: var(--violet); font-size: 10px; font-weight: 850; letter-spacing: .16em; }
  h2 { max-width: 500px; margin: 0; font-family: Georgia, serif; font-size: clamp(37px, 5vw, 50px); font-weight: 400; letter-spacing: -.04em; line-height: .96; }
  .intro { max-width: 515px; margin: 18px 0 28px; color: #6b625a; font-size: 14px; line-height: 1.55; }
  .managed-method { width: 100%; font: inherit; cursor: pointer; display: grid; grid-template-columns: 42px 1fr auto; gap: 14px; align-items: center; min-height: 82px; padding: 16px 18px; border: 1px solid #211d18; background: #211d18; color: white; text-align: left; text-decoration: none; }
  .managed-method .method-index { display: grid; place-items: center; width: 42px; height: 42px; border: 1px solid color-mix(in srgb, var(--gold) 60%, transparent); border-radius: 50%; color: var(--gold); font-size: 14px; }
  .managed-method .method-copy strong { font-size: 20px; }
  .managed-method .method-copy small { color: #cfc5ba; }
  .managed-method > i { display: grid; place-items: center; width: 30px; height: 30px; border-radius: 50%; background: var(--gold); color: #211d18; font-style: normal; }
  .alternative-methods { margin-top: 14px; border-block: 1px solid #d4cabe; }
  .alternative-methods summary { display: grid; grid-template-columns: 1fr auto 24px; gap: 10px; align-items: center; min-height: 54px; padding: 10px 3px; color: #39312b; cursor: pointer; list-style: none; }
  .alternative-methods summary::-webkit-details-marker { display: none; }
  .alternative-methods summary > span { font-family: Georgia, serif; font-size: 17px; }
  .alternative-methods summary > small { color: #776e65; font-size: 11px; }
  .alternative-methods summary > i { font-size: 16px; font-style: normal; text-align: center; transition: transform .16s ease; }
  .alternative-methods[open] summary > i { transform: rotate(180deg); }
  .methods { display: grid; border-top: 1px solid #d4cabe; }
  .methods > :is(button, a) { display: grid; grid-template-columns: 30px 1fr auto; gap: 12px; align-items: center; width: 100%; padding: 17px 3px; border: 0; border-bottom: 1px solid #d4cabe; background: transparent; color: #27211c; text-align: left; text-decoration: none; cursor: pointer; }
  .method-index { color: var(--violet); font: italic 16px Georgia, serif; }
  .method-copy { display: grid; gap: 4px; }
  .method-copy strong { font-family: Georgia, serif; font-size: 18px; font-weight: 400; }
  .method-copy small { color: #776e65; font-size: 13px; }
  .methods i { display: grid; place-items: center; width: 25px; height: 25px; border-radius: 50%; background: #eee7de; font-size: 12px; font-style: normal; }
  label { display: block; margin: 30px 0 8px; font-size: 11px; font-weight: 800; letter-spacing: .11em; text-transform: uppercase; }
  textarea { width: 100%; resize: vertical; padding: 14px; border: 1px solid #bfb3a6; border-radius: 0; background: #f8f3eb; color: #29231e; font: 12px ui-monospace, SFMono-Regular, Menlo, monospace; }
  .secret-input { width: 100%; min-height: 48px; padding: 12px 14px; border: 1px solid #bfb3a6; background: #f8f3eb; color: #29231e; font: 16px ui-monospace, SFMono-Regular, Menlo, monospace; }
  .remember { display: flex; align-items: center; gap: 9px; margin-top: 16px; text-transform: none; letter-spacing: 0; }
  .remember input { width: 18px; height: 18px; }
  .field-note { display: flex; align-items: center; gap: 8px; margin: 11px 0 0; color: #766c63; font-size: 13px; }
  .field-note span { display: grid; place-items: center; width: 17px; height: 17px; border: 1px solid #b5aa9e; border-radius: 50%; color: var(--coral-dark); font-size: 9px; font-weight: 800; }
  .actions, .connect-actions { display: flex; justify-content: flex-end; flex-wrap: wrap; gap: 10px; margin-top: 24px; }
  .primary, .secondary { display: inline-flex; align-items: center; gap: 16px; border: 0; border-radius: 999px; padding: 11px 17px; font-weight: 750; text-decoration: none; cursor: pointer; }
  .primary { background: #211d18; color: white; }
  .primary span { color: var(--gold); }
  .secondary { border: 1px solid #c9beb2; background: transparent; color: #312b26; }
  button:disabled { cursor: not-allowed; opacity: .5; }
  .connect-uri { display: grid; gap: 13px; }
  .connect-uri code { max-height: 138px; overflow: auto; padding: 16px; border: 1px solid #c8bbae; background: #f4ede3; color: #51483f; font: 10px/1.5 ui-monospace, SFMono-Regular, Menlo, monospace; overflow-wrap: anywhere; }
  .waiting { display: flex; align-items: center; gap: 9px; margin-top: 18px; color: #766c63; font-size: 13px; }
  .waiting i { width: 7px; height: 7px; border-radius: 50%; background: var(--aqua); box-shadow: 0 0 0 4px color-mix(in srgb, var(--aqua) 16%, transparent); animation: pulse 1.4s ease-in-out infinite; }
  .error { display: grid; gap: 3px; margin: 18px 0 0; padding: 12px 14px; border-left: 3px solid #a94435; background: #f8ebe7; color: #803329; font-size: 13px; line-height: 1.45; }
  .error strong { font-size: 10px; letter-spacing: .1em; text-transform: uppercase; }
  @keyframes pulse { 50% { opacity: .4; transform: scale(.75); } }
  @media (hover: hover) { .managed-method:hover { border-color: var(--aqua); box-shadow: 0 7px 0 color-mix(in srgb, var(--aqua) 45%, transparent); transform: translateY(-2px); } .alternative-methods summary:hover { color: var(--violet-dark); } .methods > :is(button, a):hover { padding-left: 9px; background: color-mix(in srgb, var(--aqua) 6%, var(--surface)); } .methods > :is(button, a):hover i { background: var(--aqua); } .close:hover, .secondary:hover { border-color: var(--aqua); } }
  @media (prefers-reduced-motion: reduce) { .waiting i { animation: none; } }
  @media (max-width: 700px) {
    .backdrop { align-items: end; padding: 0; }
    .dialog { width: 100%; max-height: 100dvh; grid-template-columns: 1fr; overflow-y: auto; overscroll-behavior: contain; border: 0; border-radius: 20px 20px 0 0; }
    .trust-panel { min-height: auto; padding: 16px 21px; border-right: 0; border-bottom: 1px solid #bfb3a5; }
    .key-seal, .trust-panel div:nth-child(3) { display: none; }
    .protocol { margin: 8px 0 0; }
    .connection-panel { padding: 34px 22px calc(26px + env(safe-area-inset-bottom)); }
    .close { top: 13px; right: 14px; width: 44px; height: 44px; }
    h2 { max-width: calc(100% - 32px); font-size: 37px; }
    .managed-method { min-height: 88px; }
    .alternative-methods summary { min-height: 58px; }
    .methods > :is(button, a) { min-height: 66px; }
    .primary, .secondary { min-height: 46px; }
    textarea { min-height: 112px; font-size: 16px; }
  }
  @media (max-width: 400px) {
    .connection-panel { padding-inline: 18px; }
    h2 { font-size: 34px; }
    .intro { margin-block: 15px 22px; }
    .managed-method { grid-template-columns: 36px 1fr auto; gap: 10px; padding-inline: 12px; }
    .managed-method .method-index { width: 36px; height: 36px; font-size: 12px; }
    .managed-method .method-copy strong { font-size: 18px; }
    .alternative-methods summary { grid-template-columns: 1fr 24px; }
    .alternative-methods summary > small { display: none; }
    .methods > :is(button, a) { grid-template-columns: 25px 1fr auto; gap: 8px; padding-block: 14px; }
    .method-copy strong { font-size: 17px; }
    .method-copy small { font-size: 12px; }
    .actions, .connect-actions { display: grid; grid-template-columns: 1fr; }
    .actions .primary, .actions .secondary, .connect-actions .primary, .connect-actions .secondary { width: 100%; justify-content: center; }
  }
</style>
